export const XLSX_MEDIA_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const XLS_MEDIA_TYPE = "application/vnd.ms-excel";

export const SAVE_MODE_BROWSER_DOWNLOAD = "browser_download";
export const SAVE_MODE_FILE_SYSTEM = "file_system";

export const BROWSER_DOWNLOAD_DESTINATION_LABEL = "Téléchargements du navigateur";
export const FILE_SYSTEM_DESTINATION_LABEL = "Emplacement choisi";

function normalizeString(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function resolveTargetWindow(targetWindow) {
  if (targetWindow !== undefined) {
    return targetWindow;
  }

  return typeof window !== "undefined" ? window : null;
}

function createSaveLocationCancelledError() {
  const error = new Error("Enregistrement annulé.");
  error.code = "save_cancelled";
  return error;
}

function buildSavePickerTypes({ fallbackFileName = "", mediaType = "" } = {}) {
  const normalizedFileName = normalizeString(fallbackFileName);
  const normalizedMediaType = normalizeString(mediaType);
  const preferLegacyWorkbook = normalizedFileName.endsWith(".xls")
    || normalizedMediaType.includes(XLS_MEDIA_TYPE);

  if (preferLegacyWorkbook && !normalizedFileName.endsWith(".xlsx")) {
    return [{
      description: "Classeur Excel 97-2003",
      accept: {
        [XLS_MEDIA_TYPE]: [".xls"],
      },
    }];
  }

  return [{
    description: "Classeur Excel",
    accept: {
      [XLSX_MEDIA_TYPE]: [".xlsx"],
      [XLS_MEDIA_TYPE]: [".xls"],
    },
  }];
}

export function isSpreadsheetSavePromptEligible({ fallbackFileName = "", mediaType = "" } = {}) {
  const normalizedFileName = normalizeString(fallbackFileName);
  const normalizedMediaType = normalizeString(mediaType);

  return normalizedFileName.endsWith(".xlsx")
    || normalizedFileName.endsWith(".xls")
    || normalizedMediaType.includes(XLSX_MEDIA_TYPE)
    || normalizedMediaType.includes(XLS_MEDIA_TYPE);
}

export function isSaveLocationCancelledError(error) {
  return Boolean(error && typeof error === "object" && error.code === "save_cancelled");
}

export async function prepareBrowserSaveTarget({
  fallbackFileName = "",
  mediaType = "",
  promptForSaveLocation = false,
  targetWindow,
} = {}) {
  const resolvedWindow = resolveTargetWindow(targetWindow);
  if (
    !promptForSaveLocation
    || !resolvedWindow
    || typeof resolvedWindow.showSaveFilePicker !== "function"
    || !isSpreadsheetSavePromptEligible({ fallbackFileName, mediaType })
  ) {
    return null;
  }

  try {
    const handle = await resolvedWindow.showSaveFilePicker({
      suggestedName: fallbackFileName || "export.xlsx",
      types: buildSavePickerTypes({ fallbackFileName, mediaType }),
    });
    if (!handle) {
      return null;
    }

    return {
      destinationLabel: FILE_SYSTEM_DESTINATION_LABEL,
      fileName: typeof handle.name === "string" && handle.name.trim()
        ? handle.name
        : fallbackFileName,
      handle,
      mediaType,
      saveMode: SAVE_MODE_FILE_SYSTEM,
      suggestedFileName: fallbackFileName,
    };
  } catch (error) {
    if (error?.name === "AbortError") {
      throw createSaveLocationCancelledError();
    }

    return null;
  }
}

export async function writeBlobToSaveTarget(blob, saveTarget) {
  if (!saveTarget?.handle || typeof saveTarget.handle.createWritable !== "function") {
    return null;
  }

  let writable = null;
  try {
    writable = await saveTarget.handle.createWritable();
    await writable.write(blob);
    await writable.close();

    return {
      destinationLabel: FILE_SYSTEM_DESTINATION_LABEL,
      fileName: typeof saveTarget.handle.name === "string" && saveTarget.handle.name.trim()
        ? saveTarget.handle.name
        : typeof saveTarget.fileName === "string" && saveTarget.fileName.trim()
          ? saveTarget.fileName
          : typeof saveTarget.suggestedFileName === "string" && saveTarget.suggestedFileName.trim()
            ? saveTarget.suggestedFileName
            : "",
      saveMode: SAVE_MODE_FILE_SYSTEM,
    };
  } catch (error) {
    if (typeof writable?.abort === "function") {
      try {
        await writable.abort();
      } catch {
        // no-op
      }
    }

    if (error?.name === "AbortError") {
      throw createSaveLocationCancelledError();
    }

    throw error;
  }
}